import type { TFunction } from "i18next";
import type { AppCategory, AppItem } from "./types";
import { FUNCTIONALITIES as CRM_FUNCTIONALITIES, functionalityRoute as crmFunctionalityRoute } from "../../../Layouts/crm/config";
import { FUNCTIONALITIES as NUVEX_FUNCTIONALITIES, functionalityRoute as nuvexFunctionalityRoute } from "../../../Layouts/nuvex-access/config";
import { FUNCTIONALITIES as STAFF_FUNCTIONALITIES, functionalityRoute as staffFunctionalityRoute } from "../../../Layouts/staff/config";
import { FUNCTIONALITIES as CARGO_FUNCTIONALITIES, functionalityRoute as cargoFunctionalityRoute } from "../../../Layouts/cargo-market/config";
import { documentacionApp } from "../../../Layouts/documentacion/nav";
import type { NuvexCategory } from "../../../Layouts/nuvex-access/config";
import { CLIENT_VIEWS, clientViewRoute, type ClientViewDef } from "../../../Layouts/client-view/config";
import { TOP_ROLE_IDS } from "../../../hooks/useIsReseller";
import { FLAGS_OPERATOR_ROLE_IDS } from "../../../hooks/useIsFlagsOperator";

type CrmFunctionality = (typeof CRM_FUNCTIONALITIES)[number];
type NuvexFunctionality = (typeof NUVEX_FUNCTIONALITIES)[number];

// Nuvex Access functionalities become apps-sidebar tiles, keeping their
// /nuvex-access/<slug> routes and `nuvex-access-<slug>` ids (so the selected-app
// highlight keeps working). They are split across two top-level categories by
// their `category` field (see Layouts/nuvex-access/config.tsx).
const nuvexAppFromFunctionality = (f: NuvexFunctionality): AppItem => ({
  id: `nuvex-access-${f.slug}`,
  name: f.name,
  description: f.description,
  icon: <i className={`${f.iconClass} text-xl`} />,
  color: f.color,
  route: nuvexFunctionalityRoute(f),
});

// Vista del cliente desde CC-CONSOLE (CC-CONSOLE #981): Horas y Equipo en
// Gestión de Staff, Gastos en Finanzas. Solo la administración de la
// plataforma las ve (el backend además lo exige en /ccTenantView/*).
// Conviven con las apps completas del mismo nombre (Equipo, Horas Trabajadas,
// Gastos y Presupuestos): el mosaico lleva «· Coding Cloud» para distinguirlas.
const clientViewApps = (t: TFunction, categoryId: ClientViewDef["categoryId"]): AppItem[] =>
  CLIENT_VIEWS.filter((v) => v.categoryId === categoryId).map((v) => ({
    id: v.appId,
    name: `${t(v.labelKey)} · ${t("clientView.header")}`,
    description: t(v.descriptionKey),
    icon: <i className={`${v.iconClass} text-xl`} />,
    color: v.color,
    route: clientViewRoute(v),
    requiredRoleIds: [...TOP_ROLE_IDS, ...(v.extraRoleIds ?? [])],
  }));

const nuvexAppsInCategory = (category: NuvexCategory): AppItem[] =>
  NUVEX_FUNCTIONALITIES.filter((f) => f.category === category).map(
    nuvexAppFromFunctionality,
  );

// CRM functionalities are surfaced as apps-sidebar tiles. Most live under the
// "crm" category, but Marketing is promoted to its own top-level category while
// keeping its /crm/<slug> route, so navigation + the selected-app highlight
// (`crm-<slug>`) keep working. (Soporte was retired in favor of the dedicated
// Service Desk app — see the "service-desk" category below.)
const crmAppFromFunctionality = (f: CrmFunctionality): AppItem => ({
  id: `crm-${f.slug}`,
  name: f.name,
  description: f.description,
  icon: <i className={`${f.iconClass} text-xl`} />,
  color: f.color,
  route: crmFunctionalityRoute(f),
});

const crmFunctionality = (slug: string): CrmFunctionality =>
  CRM_FUNCTIONALITIES.find((f) => f.slug === slug)!;

// Slugs promoted out of the CRM category into their own top-level categories.
const PROMOTED_CRM_SLUGS = ["marketing"];

// Display order of the category sections in the AppsSidebar (top → bottom).
// The "Alertas y Notificaciones" section renders above these (see AppsSidebar).
// Environment/role visibility is applied separately and preserves this order.
// Categories not listed here fall back to the end.
const CATEGORY_ORDER = [
  "rastreo-telemetria", // Administración de Flotillas
  "point-of-sale",      // Punto de Venta
  "crm",                // CRM
  "marketing",          // Marketing
  "service-desk",       // Service Desk
  "envios",             // Gestión de Envíos
  "administracion",     // Administración de la cuenta
  "internos",           // Sistemas Internos
  "bases-y-propiedades",// Bases y Propiedades (Amenidades, Patio, Reservas)
  "access-control",     // Nuvex Access — Control de acceso, seguridad y dispositivos
  "comunity",           // Nuvex Access — Comunidad, comunicación y pagos
  "cargo-market",       // Cargo Market — marketplace y cotizador de carga
  "freight-forwarding", // Freight forwarding — Documentación (expediente documental de embarques)
  "gestion-staff",      // Gestión de Staff — Actividades, Equipo, Nómina y Horas Trabajadas
  "finanzas",           // Finanzas internas — Gastos y Presupuestos (solo lista de acceso) + vista del cliente (CC-CONSOLE #981)
  "b2c",                // B2C — panel interno de operación de Movik
];

export const getAppCategories = (t: TFunction): AppCategory[] => {
  const categories: AppCategory[] = [
  {
    id: "rastreo-telemetria",
    title: t("categories.rastreo-telemetria.title"),
    description: t("categories.rastreo-telemetria.description"),
    icon: "ri-truck-line",
    apps: [
      {
        id: "lite",
        name: t("apps.lite.name"),
        description: t("apps.lite.description"),
        icon: (
          <i className="ri-map-2-line text-xl" />
        ),
        color: "bg-primary-500",
        route: "/map",
      },
      {
        id: "admin",
        name: t("apps.admin.name"),
        description: t("apps.admin.description"),
        icon: <i className="ri-truck-line text-xl" />,
        color: "bg-blue-500",
        route: "/admin/units",
      },
      {
        id: "reportes",
        name: t("apps.reportes.name"),
        description: t("apps.reportes.description"),
        icon: (
          <i className="ri-file-chart-line text-xl" />
        ),
        color: "bg-orange-500",
        // A la portada, no a una seccion suelta. Apuntaba a `/reports/history`
        // —la primera de quince— asi que quien entraba a Reportes caia en el
        // recorrido sin haber elegido nada y la portada no se veia nunca.
        route: "/reports",
      },
      {
        id: "envios-link",
        name: t("apps.envios.name"),
        description: t("apps.envios.description"),
        icon: (
          <i className="ri-inbox-line text-xl" />
        ),
        color: "bg-cyan-500",
        route: "/envios/todos",
      },
      // Torre de Control: oculta del menú mientras no tenga funcionalidad.
      // {
      //   id: "torre-control",
      //   name: t("apps.torre-control.name"),
      //   description: t("apps.torre-control.description"),
      //   icon: (
      //     <i className="ri-broadcast-line text-xl" />
      //   ),
      //   color: "bg-red-500",
      //   route: "/torre-control",
      //   badge: "Pro",
      //   badgeColor: "bg-orange-400",
      //   isLocked: true,
      // },
      // {
      //   id: "oasis",
      //   name: t("apps.oasis.name"),
      //   description: t("apps.oasis.description"),
      //   icon: (
      //     <i className="ri-building-2-line text-xl" />
      //   ),
      //   color: "bg-teal-500",
      //   route: "/oasis/yards",
      //   badge: "Pro",
      //   badgeColor: "bg-orange-400",
      //   isLocked: true,
      // },
      {
        id: "truck-yard",
        name: t("apps.truck-yard.name"),
        description: t("apps.truck-yard.description"),
        icon: <i className="ri-building-2-line text-xl" />,
        color: "bg-teal-500",
        route: "/truck-yard",
      },
      {
        id: "inspecciones",
        name: t("apps.inspecciones.name"),
        description: t("apps.inspecciones.description"),
        icon: <i className="ri-search-line text-xl" />,
        color: "bg-indigo-500",
        route: "/inspecciones",
      },
      {
        id: "fleet-integradores",
        name: t("apps.integradores.name"),
        description: t("apps.integradores.description"),
        icon: <i className="ri-plug-line text-xl" />,
        color: "bg-cyan-600",
        route: "/flota/integradores",
      },
      {
        id: "mantenimiento-vehiculos",
        name: t("apps.mantenimiento-vehiculos.name"),
        description: t("apps.mantenimiento-vehiculos.description"),
        icon: <i className="ri-tools-line text-xl" />,
        color: "bg-orange-500",
        route: "/mantenimiento/resumen",
        badge: "New",
        badgeColor: "bg-orange-400",
      },
      {
        id: "finanzas-operativas",
        name: t("apps.finanzas-operativas.name"),
        description: t("apps.finanzas-operativas.description"),
        icon: <i className="ri-calculator-line text-xl" />,
        color: "bg-emerald-500",
        route: "/finanzas-operativas/cotizaciones",
        badge: "New",
        badgeColor: "bg-emerald-400",
      },
      {
        id: "monitoreo-ia",
        name: t("apps.monitoreo-ia.name"),
        description: t("apps.monitoreo-ia.description"),
        icon: <i className="ri-shield-check-line text-xl" />,
        color: "bg-purple-500",
        route: "/monitoreo-ia/resumen",
        badge: "New",
        badgeColor: "bg-purple-400",
      },
      {
        id: "radar-correo",
        name: t("apps.radar-correo.name"),
        description: t("apps.radar-correo.description"),
        icon: <i className="ri-mail-star-line text-xl" />,
        color: "bg-teal-500",
        route: "/radar-correo/conexion",
        isBeta: true,
        badge: "Beta",
        badgeColor: "bg-teal-400",
      },
      {
        id: "chat-ia",
        name: t("apps.chat-ia.name"),
        description: t("apps.chat-ia.description"),
        icon: <i className="ri-chat-smile-3-line text-xl" />,
        color: "bg-purple-500",
        route: "/chat-ia",
        badge: "New",
        badgeColor: "bg-purple-400",
      },
      {
        id: "passenger-management",
        name: t("apps.passenger-management.name"),
        description: t("apps.passenger-management.description"),
        icon: <i className="ri-bus-line text-xl" />,
        color: "bg-sky-500",
        route: "/passengers/vehicles",
        badge: "New",
        badgeColor: "bg-sky-400",
      },
      {
        id: "papelera",
        name: t("apps.papelera.name"),
        description: t("apps.papelera.description"),
        icon: <i className="ri-delete-bin-line text-xl" />,
        color: "bg-red-600",
        route: "/papelera",
      },
    ],
  },
  {
    id: "point-of-sale",
    title: t("categories.point-of-sale.title"),
    description: t("categories.point-of-sale.description"),
    icon: "ri-store-2-line",
    apps: [
      {
        id: "pos-cashier",
        name: t("apps.pos-cashier.name"),
        description: t("apps.pos-cashier.description"),
        icon: (
          <i className="ri-money-dollar-box-line text-xl" />
        ),
        color: "bg-emerald-500",
        route: "/pos/cashier/terminal",
        badge: "New",
        badgeColor: "bg-emerald-400",
      },
      {
        id: "pos-products",
        name: t("apps.pos-products.name"),
        description: t("apps.pos-products.description"),
        icon: <i className="ri-box-3-line text-xl" />,
        color: "bg-blue-500",
        route: "/pos/products/list",
      },
      {
        id: "pos-inventory",
        name: t("apps.pos-inventory.name"),
        description: t("apps.pos-inventory.description"),
        icon: (
          <i className="ri-archive-line text-xl" />
        ),
        color: "bg-amber-500",
        route: "/pos/inventory/stock",
      },
      {
        id: "pos-coupons",
        name: t("apps.pos-coupons.name"),
        description: t("apps.pos-coupons.description"),
        icon: (
          <i className="ri-coupon-line text-xl" />
        ),
        color: "bg-pink-500",
        route: "/pos/coupons/list",
      },
      {
        id: "pos-orders",
        name: t("apps.pos-orders.name"),
        description: t("apps.pos-orders.description"),
        icon: (
          <i className="ri-receipt-line text-xl" />
        ),
        color: "bg-indigo-500",
        route: "/pos/orders/sales",
      },
      {
        id: "pos-ecommerce",
        name: t("apps.pos-ecommerce.name"),
        description: t("apps.pos-ecommerce.description"),
        icon: <i className="ri-store-2-line text-xl" />,
        color: "bg-violet-500",
        route: "/pos/ecommerce/dashboard",
      },
    ],
  },
  {
    id: "administracion",
    title: t("categories.administracion.title"),
    description: t("categories.administracion.description"),
    icon: "ri-settings-3-line",
    apps: [
      {
        id: "facturacion",
        name: t("apps.facturacion.name"),
        description: t("apps.facturacion.description"),
        icon: (
          <i className="ri-file-text-line text-xl" />
        ),
        color: "bg-purple-500",
        route: "/facturacion/resumen",
      },
      {
        id: "servicios",
        name: t("apps.servicios.name"),
        description: t("apps.servicios.description"),
        icon: <i className="ri-computer-line text-xl" />,
        color: "bg-indigo-500",
        route: "/entrega-servicio",
      },
      {
        id: "servicios-tecnicos",
        name: t("apps.servicios-tecnicos.name"),
        description: t("apps.servicios-tecnicos.description"),
        icon: (
          <i className="ri-tools-line text-xl" />
        ),
        color: "bg-gray-600",
        route: "/servicios-tecnicos",
        isComingSoon: true,
      },
      {
        id: "ajustes",
        name: t("apps.ajustes.name"),
        description: t("apps.ajustes.description"),
        icon: <i className="ri-settings-3-line text-xl" />,
        color: "bg-slate-500",
        route: "/ajustes",
        isComingSoon: true,
      },
      {
        id: "settings",
        name: t("apps.settings.name"),
        description: t("apps.settings.description"),
        icon: (
          <i className="ri-graduation-cap-line text-xl" />
        ),
        color: "bg-sky-500",
        // Ayuda y Tutoriales (#1015). El id se conserva por el catálogo de derechos.
        route: "/ayuda",
        isBeta: true,
      },
      {
        id: "support",
        name: t("apps.support.name"),
        description: t("apps.support.description"),
        icon: (
          <i className="ri-customer-service-2-line text-xl" />
        ),
        color: "bg-rose-500",
        route: "/support",
      },
    ],
  },
  {
    // CRM — sales/marketing surface. Front-end scaffold (mock data) except
    // "Leads → Bandeja de leads", which reads real leads captured from the
    // marketing landing's contact form. Marketing and Soporte are promoted to
    // the Marketing / Service Desk categories below, so they're excluded here.
    // Visible in dev + prod (see environmentVisibility.config.ts).
    id: "crm",
    title: t("categories.crm.title"),
    description: t("categories.crm.description"),
    icon: "ri-customer-service-2-line",
    apps: CRM_FUNCTIONALITIES.filter(
      (f) => !PROMOTED_CRM_SLUGS.includes(f.slug),
    )
      .map(crmAppFromFunctionality)
      // Prospectos de Wayak leídos en vivo de CC-CONSOLE (CC-CONSOLE #1415).
      .concat(clientViewApps(t, "crm")),
  },
  {
    id: "internos",
    title: t("categories.internos.title"),
    description: t("categories.internos.description"),
    icon: "ri-tools-line",
    requiredRoleIds: TOP_ROLE_IDS,
    apps: [
      {
        id: "reseller-admin",
        name: t("apps.reseller-admin.name"),
        description: t("apps.reseller-admin.description"),
        icon: <i className="ri-apps-2-line text-xl" />,
        color: "bg-blue-600",
        route: "/reseller-admin",
        isBeta: true,
      },
      {
        // Feature Flags (épica #1219): prende y apaga las banderas globales de
        // WAYAK. Sólo operadores de banderas (admin de plataforma, owner y
        // developer); la API lo exige además (requireFlagsOperator).
        id: "feature-flags",
        name: t("apps.feature-flags.name"),
        description: t("apps.feature-flags.description"),
        icon: <i className="ri-toggle-line text-xl" />,
        color: "bg-rose-600",
        route: "/feature-flags",
        requiredRoleIds: FLAGS_OPERATOR_ROLE_IDS,
        isBeta: true,
      },
      {
        // Catálogo de licencias del reseller: qué planes existen, qué incluye
        // cada uno y cuáles se publican en el landing (#412). Se cobran en la
        // cuenta Stripe del reseller, no en la del tenant (esa es la del POS).
        id: "licencias",
        name: t("apps.licencias.name"),
        description: t("apps.licencias.description"),
        icon: <i className="ri-key-2-line text-xl" />,
        color: "bg-amber-500",
        route: "/licencias",
        isBeta: true,
      },
      {
        // Analíticas (id histórico `comportamiento`, CC-CONSOLE #1397): tablero
        // general, todos los usuarios de WAYAK y la ficha de comportamiento de
        // cada uno (PostHog). Sólo owner y admin reseller: la ruta y la API
        // exigen lo mismo.
        id: "comportamiento",
        name: t("apps.comportamiento.name"),
        description: t("apps.comportamiento.description"),
        icon: <i className="ri-line-chart-line text-xl" />,
        color: "bg-indigo-600",
        route: "/comportamiento",
        requiredRoleIds: TOP_ROLE_IDS,
        isBeta: true,
      },
      // Movik (CC-CONSOLE #1535) ya no es un mosaico de Sistemas Internos: su
      // vista de sólo lectura es la sección «Marca y altas» de la app Movik de
      // la categoría B2C (/b2c/movik/marca; /movik redirige allá).
      {
        id: "cobranza",
        name: t("apps.cobranza.name"),
        description: t("apps.cobranza.description"),
        icon: (
          <i className="ri-clipboard-line text-xl" />
        ),
        color: "bg-emerald-500",
        route: "/cobranza",
        isBeta: true,
      },
      {
        id: "dashboards",
        name: t("apps.dashboards.name"),
        description: t("apps.dashboards.description"),
        icon: (
          <i className="ri-pie-chart-line text-xl" />
        ),
        color: "bg-violet-500",
        route: "/dashboards",
        badge: "Beta",
        badgeColor: "bg-emerald-400",
      },
      {
        id: "integradores",
        name: t("apps.integradores.name"),
        description: t("apps.integradores.description"),
        icon: <i className="ri-plug-line text-xl" />,
        color: "bg-cyan-600",
        route: "/integradores",
        badge: "Beta",
        badgeColor: "bg-emerald-400",
      },
      {
        id: "papelera-interno",
        name: t("apps.papelera-interno.name"),
        description: t("apps.papelera-interno.description"),
        icon: <i className="ri-delete-bin-line text-xl" />,
        color: "bg-red-600",
        route: "/papelera-interno",
      },
      {
        id: "service-desk",
        name: t("apps.service-desk.name"),
        description: t("apps.service-desk.description"),
        icon: <i className="ri-lifebuoy-line text-xl" />,
        color: "bg-cyan-500",
        route: "/service-desk",
        badge: "Beta",
        badgeColor: "bg-emerald-400",
      },
      {
        id: "respaldos",
        name: t("apps.respaldos.name"),
        description: t("apps.respaldos.description"),
        icon: <i className="ri-box-3-line text-xl" />,
        color: "bg-slate-600",
        route: "/respaldos",
      },
      {
        id: "compras",
        name: t("apps.compras.name"),
        description: t("apps.compras.description"),
        icon: (
          <i className="ri-building-2-line text-xl" />
        ),
        color: "bg-amber-500",
        route: "/compras",
        isComingSoon: true,
      },
      {
        id: "logistica",
        name: t("apps.logistica.name"),
        description: t("apps.logistica.description"),
        icon: <i className="ri-truck-line text-xl" />,
        color: "bg-cyan-500",
        route: "/logistica",
        isComingSoon: true,
      },
    ],
  },
  {
    // Marketing — promoted from CRM "Marketing" (campañas, formularios, landing pages).
    id: "marketing",
    title: t("categories.marketing.title"),
    description: t("categories.marketing.description"),
    icon: "ri-megaphone-line",
    apps: [crmAppFromFunctionality(crmFunctionality("marketing"))],
  },
  {
    // Service Desk — the Mesa de Servicio app (tickets, Kanban, SLA, métricas,
    // portal público). The legacy CRM "Soporte" scaffold was retired in favor
    // of this full app.
    id: "service-desk",
    title: t("categories.service-desk.title"),
    description: t("categories.service-desk.description"),
    icon: "ri-headphone-line",
    apps: [
      {
        // Mesa de Servicio — multi-brand ticketing workspace (Kanban, SLA,
        // dashboard, GitHub linking). Full app; see Layouts/service-desk.
        id: "service-desk",
        name: "Mesa de Servicio",
        description: "Ticketing multimarca, Kanban, SLA y métricas",
        icon: <i className="ri-customer-service-2-line text-xl" />,
        color: "bg-indigo-500",
        route: "/service-desk/dashboard",
      },
    ],
  },
  {
    // Envíos — Gestión y rastreo de envíos
    id: "envios",
    title: t("categories.envios.title"),
    description: t("categories.envios.description"),
    icon: "ri-truck-line",
    apps: [
      {
        id: "envios",
        name: t("apps.envios.name"),
        description: t("apps.envios.description"),
        icon: <i className="ri-inbox-line text-xl" />,
        color: "bg-cyan-500",
        route: "/envios/todos",
      },
    ],
  },
  {
    // Bases y Propiedades — reservable spaces product surface. Visible to ANY
    // tenant in every fork (no company allowlist, no hardcoded companyId).
    //   - Amenidades: the "what" (profile, price, rules, photos, availability)
    //   - Patio de Maniobras: the existing app, now multi-tenant (gate removed)
    //   - Reservas: the "when/who" (calendar + staff booker + public booking)
    id: "bases-y-propiedades",
    title: t("categories.bases-y-propiedades.title"),
    description: t("categories.bases-y-propiedades.description"),
    icon: "ri-building-2-line",
    apps: [
      {
        id: "bases",
        name: t("apps.bases.name"),
        description: t("apps.bases.description"),
        icon: <i className="ri-building-4-line text-xl" />,
        color: "bg-indigo-500",
        route: "/bases",
        badge: "New",
        badgeColor: "bg-emerald-400",
      },
      {
        id: "amenidades",
        name: t("apps.amenidades.name"),
        description: t("apps.amenidades.description"),
        icon: <i className="ri-building-line text-xl" />,
        color: "bg-emerald-500",
        route: "/amenidades",
        badge: "New",
        badgeColor: "bg-emerald-400",
      },
      {
        id: "truck-yard",
        name: t("apps.truck-yard.name"),
        description: t("apps.truck-yard.description"),
        icon: <i className="ri-building-2-line text-xl" />,
        color: "bg-teal-500",
        route: "/truck-yard",
      },
      {
        id: "reservas",
        name: t("apps.reservas.name"),
        description: t("apps.reservas.description"),
        icon: <i className="ri-calendar-check-line text-xl" />,
        color: "bg-amber-500",
        route: "/reservas",
        badge: "New",
        badgeColor: "bg-emerald-400",
      },
      {
        // Rondines (CC-CONSOLE #1592): rutas, puntos de control, rondas e incidencias.
        id: "rondines",
        name: t("apps.rondines.name"),
        description: t("apps.rondines.description"),
        icon: <i className="ri-shield-check-line text-xl" />,
        color: "bg-sky-600",
        route: "/rondines",
        badge: "New",
        badgeColor: "bg-emerald-400",
      },
      {
        // Activos (épica común CC-CONSOLE ↔ WAYAK, #1638): inventario, compras,
        // proveedores y mantenimiento. Beta y con bandera `off` (catálogo v26,
        // D8): sólo se ve con `app:activos` encendido y el preset del tenant.
        // Pantallas en Layouts/activos (PR 3).
        id: "activos",
        name: t("apps.activos.name"),
        description: t("apps.activos.description"),
        icon: <i className="ri-stack-line text-xl" />,
        color: "bg-purple-500",
        route: "/activos",
      },
    ],
  },
  {
    // Access Control — control de acceso, seguridad y dispositivos. Promoted
    // from the legacy "Nuvex Access" group; keeps /nuvex-access/<slug> routes.
    // Front-end scaffold (mock data); visible per environmentVisibility.config.ts.
    id: "access-control",
    title: t("categories.access-control.title"),
    description: t("categories.access-control.description"),
    icon: "ri-door-lock-box-line",
    apps: nuvexAppsInCategory("access-control"),
  },
  {
    // Comunity — gestión de comunidad, comunicación y pagos. Promoted from the
    // legacy "Nuvex Access" group; keeps /nuvex-access/<slug> routes.
    // Front-end scaffold (mock data); visible per environmentVisibility.config.ts.
    id: "comunity",
    title: t("categories.comunity.title"),
    description: t("categories.comunity.description"),
    icon: "ri-community-line",
    apps: nuvexAppsInCategory("comunity"),
  },
  {
    // Cargo Market — bolsa de carga ligada a la flotilla (Embarcador,
    // Transportista, Administración del mercado) y cotizador aéreo/marítimo;
    // visible per environmentVisibility.config.ts.
    id: "cargo-market",
    title: t("categories.cargo-market.title"),
    description: t("categories.cargo-market.description"),
    icon: "ri-ship-2-line",
    apps: CARGO_FUNCTIONALITIES.map((f) => ({
      id: `cargo-market-${f.slug}`,
      name: f.name,
      description: f.description,
      icon: <i className={`${f.iconClass} text-xl`} />,
      color: f.color,
      route: cargoFunctionalityRoute(f),
      ...(f.requiredRoleIds ? { requiredRoleIds: f.requiredRoleIds } : {}),
    })),
  },
  {
    // Freight forwarding — Documentación (CC-CONSOLE #1632): expediente
    // documental de envíos y reservas (checklist, versiones, vencimientos,
    // plantillas). Gating: preset freight-forwarding/documentacion + catálogo
    // app:documentacion + guard /documentacion (config/access/routes.ts).
    id: "freight-forwarding",
    title: t("categories.freight-forwarding.title"),
    description: t("categories.freight-forwarding.description"),
    icon: "ri-folder-shield-2-line",
    apps: [
      {
        id: documentacionApp.id,
        name: t("apps.documentacion.name"),
        description: t("apps.documentacion.description"),
        icon: <i className={`${documentacionApp.iconClass} text-xl`} />,
        color: documentacionApp.color,
        route: documentacionApp.route,
        isBeta: true,
      },
    ],
  },
  {
    // Gestión de Staff — field-staff activity reporting with evidence
    // (descripción + archivos + cámara + geolocalización), linkable to an
    // amenity (NUVEX) or vehicle (Fleet Manager). Front-end scaffold; visible
    // per environmentVisibility.config.ts.
    id: "gestion-staff",
    title: t("categories.gestion-staff.title"),
    description: t("categories.gestion-staff.description"),
    icon: "ri-team-line",
    apps: [
      ...STAFF_FUNCTIONALITIES.map((f) => ({
        id: `gestion-staff-${f.slug}`,
        name: f.name,
        description: f.description,
        icon: <i className={`${f.iconClass} text-xl`} />,
        color: f.color,
        route: staffFunctionalityRoute(f),
      })),
      {
        // Recursos Humanos — Equipo (colaboradores, squads, tarifas, contratistas).
        // Portada de CC-CONSOLE; useFilteredCategories la oculta hasta que el
        // servidor confirma quién la administra.
        id: "team",
        name: t("apps.team.name"),
        description: t("apps.team.description"),
        icon: <i className="ri-group-line text-xl" />,
        color: "bg-lime-600",
        route: "/team/summary",
      },
      {
        // Recursos Humanos — Nómina (asistencia, incidencias, ajustes, recibos)
        id: "payroll",
        name: t("apps.payroll.name"),
        description: t("apps.payroll.description"),
        icon: <i className="ri-bank-card-line text-xl" />,
        color: "bg-emerald-600",
        // CC-CONSOLE #1025: se entra por «Mis pagos», lo que cada colaborador viene a ver.
        route: "/payroll/payments",
      },
      {
        // Horas Trabajadas — horas por commits, heatmap y reportes (CC-CONSOLE #528)
        id: "work-hours",
        name: t("apps.work-hours.name"),
        description: t("apps.work-hours.description"),
        icon: <i className="ri-timer-line text-xl" />,
        color: "bg-violet-600",
        route: "/work-hours/dashboard",
      },
      ...clientViewApps(t, "gestion-staff"),
    ],
  },
  {
    // Finanzas internas — Gastos y Presupuestos (CC-CONSOLE #615). Además del
    // preset del tenant, useFilteredCategories la oculta a quien no está en la
    // lista de acceso del módulo (lo decide el servidor). Aquí vive también lo
    // que Coding Cloud atribuye a este cliente (vista del cliente, #981).
    id: "finanzas",
    title: t("categories.finanzas.title"),
    description: t("categories.finanzas.description"),
    icon: "ri-money-dollar-circle-line",
    apps: [
      {
        id: "expenses",
        name: t("apps.expenses.name"),
        description: t("apps.expenses.description"),
        icon: <i className="ri-file-list-3-line text-xl" />,
        color: "bg-teal-600",
        route: "/expenses/summary",
        isBeta: true,
      },
      {
        // Configuración de Finanzas internas (CC-CONSOLE #1255): categorías y tipo de cambio.
        id: "finance-settings",
        name: t("apps.finance-settings.name"),
        description: t("apps.finance-settings.description"),
        icon: <i className="ri-equalizer-line text-xl" />,
        color: "bg-slate-600",
        route: "/finance-settings/categories",
        isBeta: true,
      },
      {
        // Cobranza (CC-CONSOLE #1243): en los tenants que la declaran en
        // «Finanzas internas» vive aquí; useFilteredCategories la quita entonces
        // de «Sistemas Internos» y conserva su puerta (sólo la plataforma).
        id: "cobranza",
        name: t("apps.cobranza.name"),
        description: t("apps.cobranza.description"),
        icon: <i className="ri-clipboard-line text-xl" />,
        color: "bg-emerald-500",
        route: "/cobranza",
        isBeta: true,
      },
      ...clientViewApps(t, "finanzas"),
    ],
  },
  {
    // B2C — panel INTERNO de operación de los productos B2C de la familia. Hoy
    // sólo Movik (acompañamiento de trayectos): quién se registró, los
    // acompañamientos, los SOS y los enlaces a su web, tienda y PWA. No es la
    // app de la persona; la ven los administradores de plataforma (la
    // categoría nace `internal` en el catálogo de derechos). En WAYAK la
    // puerta del sidebar es `requiredRoleIds`, como Sistemas Internos.
    id: "b2c",
    title: t("categories.b2c.title"),
    description: t("categories.b2c.description"),
    icon: "ri-shield-user-line",
    requiredRoleIds: TOP_ROLE_IDS,
    apps: [
      {
        // Misma llave `app:movik` que la vista de Sistemas Internos de #1535:
        // en la v10 del catálogo se movió a esta categoría (su vista es ahora
        // la sección «Marca y altas»). La ruta y la API exigen plataforma.
        id: "movik",
        name: t("apps.movik.name"),
        description: t("apps.movik.description"),
        icon: <i className="ri-walk-line text-xl" />,
        color: "bg-rose-500",
        route: "/b2c/movik",
        requiredRoleIds: TOP_ROLE_IDS,
        isBeta: true,
      },
    ],
  },
  ];

  const rank = (id: string) => {
    const i = CATEGORY_ORDER.indexOf(id);
    return i === -1 ? CATEGORY_ORDER.length : i;
  };
  return [...categories].sort((a, b) => rank(a.id) - rank(b.id));
};
